import {
  pickRounds, scopeBBox, scopeSpanKm, scaleFor, scoreDistance, speedBonus, haversineKm, wrapLng,
  type GameConfig, type Place, type ScoreScale, type BBox,
} from "../../shared/game.ts";
import type { Catalog } from "./catalog.ts";

/** What the play/reveal screens need to know, whether the game runs locally or on the server. */
export interface Player { id: string; name: string; color: string; score: number; hasGuessed: boolean;
  guess?: { lat: number; lng: number } | null; distanceKm?: number; points?: number; bonus?: number; hint?: boolean; isHost?: boolean; rank?: number }

export interface RoundView {
  phase: "playing" | "reveal" | "ended";
  round: number;            // 0-based
  totalRounds: number;
  place: Pick<Place, "id" | "n" | "t" | "c" | "r"> & { lat?: number; lng?: number };
  endsAt: number;           // ms epoch; 0 = no timer
  timerSec: number;
  nextAt: number;           // reveal → next round (multiplayer)
  scale: ScoreScale;
  bbox: BBox;
  players: Player[];          // multiplayer: the leaders, closest, host and you — not everyone in a big room
  me: string;
  playerCount?: number;
  guessedCount?: number;
}

export interface RoundResult { placeId: string; name: string; distanceKm: number; points: number; bonus: number; hint: boolean;
  guess: { lat: number; lng: number } | null; answer: { lat: number; lng: number }; ms: number }

export interface GameController {
  readonly multiplayer: boolean;
  view(): RoundView;
  guess(lat: number, lng: number, usedHint: boolean): void;
  next(): void;            // solo: advance; multiplayer: no-op (server drives)
  quit(): void;
  results(): RoundResult[];
  onChange(fn: () => void): void;
}

export const HINT_COST = 150;

/** Solo game: everything runs in the browser. */
export class SoloGame implements GameController {
  readonly multiplayer = false;
  private rounds: Place[];
  private i = 0;
  private phase: RoundView["phase"] = "playing";
  private startedAt = 0;
  private endsAt = 0;
  private timer = 0;
  private res: RoundResult[] = [];
  private listeners: (() => void)[] = [];
  private scale: ScoreScale;
  private bbox: BBox;
  private me: Player;

  constructor(private cfg: GameConfig, private cat: Catalog, name: string, color: string, fixedRounds?: Place[]) {
    this.rounds = fixedRounds ?? pickRounds(cat.places, cfg, cat.continentOf);
    this.bbox = scopeBBox(cfg.scope, cat.index);
    this.scale = scaleFor(scopeSpanKm(this.bbox));
    this.me = { id: "me", name, color, score: 0, hasGuessed: false };
    this.startRound();
  }

  get count() { return this.rounds.length; }
  onChange(fn: () => void) { this.listeners.push(fn); }
  private emit() { this.listeners.forEach(f => f()); }

  private startRound() {
    this.phase = "playing";
    this.me.hasGuessed = false; this.me.guess = null;
    this.startedAt = Date.now();
    this.endsAt = this.cfg.timer ? this.startedAt + this.cfg.timer * 1000 : 0;
    clearTimeout(this.timer);
    if (this.endsAt) this.timer = window.setTimeout(() => this.finishRound(null, false), this.cfg.timer * 1000 + 50);
  }

  guess(lat: number, lng: number, usedHint: boolean) {
    if (this.phase !== "playing") return;
    this.finishRound({ lat, lng: wrapLng(lng) }, usedHint);
  }

  private finishRound(g: { lat: number; lng: number } | null, usedHint: boolean) {
    if (this.phase !== "playing") return;
    clearTimeout(this.timer);
    const p = this.rounds[this.i];
    const ms = Date.now() - this.startedAt;
    const dist = g ? haversineKm(g.lat, g.lng, p.lat, p.lng) : -1;
    let points = g ? scoreDistance(dist, this.scale) : 0;
    if (usedHint) points = Math.max(0, points - HINT_COST);
    const bonus = g ? speedBonus(points, ms, this.cfg.timer) : 0;
    this.res.push({ placeId: p.id, name: p.n, distanceKm: dist, points, bonus, hint: usedHint, guess: g, answer: { lat: p.lat, lng: p.lng }, ms });
    this.me.score += points + bonus;
    Object.assign(this.me, { hasGuessed: !!g, guess: g, distanceKm: dist, points, bonus, hint: usedHint });
    this.phase = "reveal";
    this.emit();
  }

  next() {
    if (this.phase !== "reveal") return;
    if (this.i >= this.rounds.length - 1) { this.phase = "ended"; this.emit(); return; }
    this.i++;
    this.startRound();
    this.emit();
  }

  quit() { clearTimeout(this.timer); this.phase = "ended"; }

  results() { return this.res; }
  roundsPlaces() { return this.rounds; }

  view(): RoundView {
    const p = this.rounds[this.i];
    const reveal = this.phase !== "playing";
    return {
      phase: this.phase, round: this.i, totalRounds: this.rounds.length,
      place: { id: p.id, n: p.n, t: p.t, c: p.c, r: p.r, ...(reveal ? { lat: p.lat, lng: p.lng } : {}) },
      endsAt: this.endsAt, timerSec: this.cfg.timer, nextAt: 0, scale: this.scale, bbox: this.bbox,
      players: [this.me], me: "me",
    };
  }
}

/** Seeded random so the daily challenge is the same for everyone on a given date. */
export function seeded(seed: string): () => number {
  let h = 1779033703 ^ seed.length;
  for (let i = 0; i < seed.length; i++) { h = Math.imul(h ^ seed.charCodeAt(i), 3432918353); h = (h << 13) | (h >>> 19); }
  return () => {
    h = Math.imul(h ^ (h >>> 16), 2246822507); h = Math.imul(h ^ (h >>> 13), 3266489909);
    return ((h ^= h >>> 16) >>> 0) / 4294967296;
  };
}

export function todayKey(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
